import React, { useState, useMemo, useEffect, useRef } from 'react';
// --- Import Icons from lucide-react ---
import { Leaf, Sun, Droplet, Filter, X, Search, ChevronDown, Landmark, Download, LayoutGrid, List, Loader2, AlertTriangle, ShieldCheck, Heart, CircleEllipsis } from 'lucide-react';

// --- PACENation Brand Colors ---
const BRAND_COLORS = {
  primaryBlue: 'bg-[#1e2d60] text-white',
  primaryBlueDark: 'bg-[#172042] text-white',
  primaryBlueHover: 'hover:bg-[#172042]',
  primaryBlueText: 'text-[#1e2d60]',
  accentGreen: 'bg-[#67b04b] text-white',
  accentGreenLight: 'bg-[#8ac77a] text-white',
  accentGreenHover: 'hover:bg-[#53903c]',
  accentGreenText: 'text-[#67b04b]',
  lightBackground: 'bg-[#f8f9fa]',
  darkText: 'text-gray-800',
};

// --- Map Project Types (Flags) to Icons/Labels ---
const PROJECT_TYPE_INFO = {
  energy_efficiency: { label: 'Energy Efficiency', Icon: Leaf, color: 'text-[#67b04b]', badgeColor: 'bg-[#e8f5e4] text-[#3e682d]' },
  renewable_energy: { label: 'Renewable Energy', Icon: Sun, color: 'text-amber-500', badgeColor: 'bg-amber-100 text-amber-800' },
  recapitalization: { label: 'Recapitalization', Icon: Droplet, color: 'text-[#1e2d60]', badgeColor: 'bg-[#e5e8f0] text-[#1e2d60]' },
  resilience: { label: 'Resilience', Icon: ShieldCheck, color: 'text-indigo-500', badgeColor: 'bg-indigo-100 text-indigo-800' },
  public_health: { label: 'Public Health', Icon: Heart, color: 'text-red-500', badgeColor: 'bg-red-100 text-red-800' },
  other: { label: 'Other', Icon: CircleEllipsis, color: 'text-gray-500', badgeColor: 'bg-gray-100 text-gray-800' }
};

// --- Map Property Types to Color Classes ---
const PROPERTY_TYPE_COLORS = {
  'Office': 'bg-blue-100 text-blue-800 border border-blue-200',
  'Industrial': 'bg-gray-100 text-gray-800 border border-gray-200',
  'Hospitality': 'bg-purple-100 text-purple-800 border border-purple-200',
  'Agricultural': 'bg-lime-100 text-lime-900 border border-lime-200',
  'Retail': 'bg-pink-100 text-pink-800 border border-pink-200',
  'Non-Profit': 'bg-cyan-100 text-cyan-800 border border-cyan-200',
  'Multifamily': 'bg-orange-100 text-orange-800 border border-orange-200',
  'Healthcare': 'bg-red-100 text-red-800 border border-red-200',
  'Entertainment': 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  'Default': 'bg-gray-100 text-gray-700 border border-gray-200'
};


// --- Helper Functions ---
const formatCurrency = (amount, compact = false) => {
  if (amount == null) return 'N/A';
  const options = { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 };
  
  // Always use compact notation for card display to prevent overflow
  if (compact || amount >= 1000000) {
    options.notation = 'compact';
    options.compactDisplay = 'short';
  }
  
  return new Intl.NumberFormat('en-US', options).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() + (offset * 60 * 1000));
    // Use shorter month format and remove day to save space
    return adjustedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return 'Invalid Date';
  }
};

// --- React Component ---
function App() {
  // --- State Variables ---
  const [allCaseStudies, setAllCaseStudies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search State
  const [searchTerm, setSearchTerm] = useState('');
  // Filter State
  const [filter_state, setFilterState] = useState('All');
  const [selectedStates, setSelectedStates] = useState([]);
  const [filter_property_type, setFilterPropertyType] = useState('All');
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState([]);
  const [filter_project_stage, setFilterProjectStage] = useState('All');
  const [selectedProjectStages, setSelectedProjectStages] = useState([]);
  const [filterTypeFlags, setFilterTypeFlags] = useState({ 
    energy_efficiency: false, 
    renewable_energy: false, 
    resilience: false,
    public_health: false,
    other: false
  });
  const [filter_min_amount, setFilterMinAmount] = useState(0);
  const [filter_completion_year, setFilterCompletionYear] = useState('All');
  const [filter_historic, setFilterHistoric] = useState(false);
  // State for searchable State dropdown
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [isStateListOpen, setIsStateListOpen] = useState(false);
  // Sort State
  const [sortKey, setSortKey] = useState('date_closed');
  const [sortOrder, setSortOrder] = useState('desc');
  // UI State
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  // Pagination
  const [itemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);

  // Ref for the state filter container
  const stateFilterRef = useRef(null);

  // --- Data Fetching Effect ---
  useEffect(() => {
    const fetchCaseStudies = async () => {
      setIsLoading(true);
      setError(null);
      console.log("Attempting to fetch from Netlify function...");
      try {
        const response = await fetch('/.netlify/functions/get-case-studies');
        console.log("Fetch response status:", response.status);

        if (!response.ok) {
           let errorMsg = `Failed to fetch: ${response.status} ${response.statusText}`;
           try {
               const errData = await response.json();
               console.error("Error data from function:", errData);
               errorMsg = errData.error || errorMsg;
           } catch (e) {
                console.error("Response was not JSON:", e);
                try {
                  const textResponse = await response.text();
                  console.error("Raw response:", textResponse);
                } catch (textErr) {
                  console.error("Could not get response text:", textErr);
                }
           }
           throw new Error(errorMsg);
        }
        const data = await response.json();
        console.log("Successfully fetched data:", data);
        
        if (!Array.isArray(data)) {
          console.error("Data is not an array:", data);
          setError("Invalid data format received from server.");
          setAllCaseStudies([]);
        } else {
          setAllCaseStudies(data);
        }
      } catch (fetchError) {
        console.error('Error fetching case studies via Netlify Function:', fetchError);
        setError(`Failed to load data. Please ensure the backend function is running correctly. Error: ${fetchError.message}`);
        setAllCaseStudies([]);
      } finally {
        setIsLoading(false);
        console.log("Finished fetching, loading set to false.");
      }
    };
    fetchCaseStudies();
  }, []);

  // --- Derived Data for Filters/Sliders ---
  const uniqueStates = useMemo(() => allCaseStudies.length > 0 ? ['All', ...new Set(allCaseStudies.map(cs => cs.state).filter(Boolean).sort())] : ['All'], [allCaseStudies]);
  const uniquePropertyTypes = useMemo(() => allCaseStudies.length > 0 ? ['All', ...new Set(allCaseStudies.map(cs => cs.property_type).filter(Boolean).sort())] : ['All'], [allCaseStudies]);
  const uniqueProjectStages = useMemo(() => allCaseStudies.length > 0 ? ['All', ...new Set(allCaseStudies.map(cs => cs.project_stage).filter(Boolean).sort())] : ['All'], [allCaseStudies]);
  const uniqueCompletionYears = useMemo(() => {
      if (allCaseStudies.length === 0) return ['All'];
      const years = new Set(allCaseStudies.map(cs => cs.date_closed ? new Date(cs.date_closed).getFullYear() : null).filter(y => y && !isNaN(y)));
      return ['All', ...Array.from(years).sort((a, b) => b - a)];
  }, [allCaseStudies]);

  const { minAmount, maxAmount } = useMemo(() => {
      if (allCaseStudies.length === 0) return { minAmount: 0, maxAmount: 1000000 };
      const amounts = allCaseStudies.map(cs => cs.financing_amount).filter(a => a != null);
      if (amounts.length === 0) return { minAmount: 0, maxAmount: 1000000 };
      return {
          minAmount: Math.min(0, ...amounts),
          maxAmount: Math.max(1, ...amounts)
      };
  }, [allCaseStudies]);

  // Filtered states for the searchable dropdown
  const filteredStates = useMemo(() => {
      if (!stateSearchTerm) return uniqueStates;
      return uniqueStates.filter(s => s.toLowerCase().includes(stateSearchTerm.toLowerCase()));
  }, [uniqueStates, stateSearchTerm]);

  // Update filter_min_amount if it exceeds the actual maxAmount
  useEffect(() => {
      if (maxAmount > 0 && filter_min_amount > maxAmount) setFilterMinAmount(maxAmount);
      else if (maxAmount === 0 && filter_min_amount !== 0) setFilterMinAmount(0);
  }, [maxAmount, filter_min_amount]);

  // Effect to handle clicks outside the state filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => { if (stateFilterRef.current && !stateFilterRef.current.contains(event.target)) setIsStateListOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [stateFilterRef]);

  // Effect to send height to parent window
  useEffect(() => {
    function sendHeightToParent() {
      // Get the document height
      const height = document.body.scrollHeight;
      
      // Send a message to the parent with the current height
      if (window.parent !== window) {
        window.parent.postMessage({ 
          type: 'resize',
          height: height 
        }, '*');
      }
    }

    // Set up observers to detect height changes
    const resizeObserver = new ResizeObserver(() => {
      sendHeightToParent();
    });

    // Observe the document body
    resizeObserver.observe(document.body);

    // Also observe on content load and window resize
    window.addEventListener('load', sendHeightToParent);
    window.addEventListener('resize', sendHeightToParent);

    // Set up mutation observer to catch DOM changes
    const mutationObserver = new MutationObserver(() => {
      sendHeightToParent();
    });

    // Start observing the document body for DOM changes
    mutationObserver.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      characterData: true
    });

    // Initial height send
    sendHeightToParent();

    // Clean up observers on unmount
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('load', sendHeightToParent);
      window.removeEventListener('resize', sendHeightToParent);
    };
  }, []);

  // --- Filtering and Sorting Logic ---
  const displayedCaseStudies = useMemo(() => {
    if (!Array.isArray(allCaseStudies)) return [];
    let filtered = [...allCaseStudies];

    // Apply Search
    if (searchTerm.trim() !== '') {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(cs => {
        // Search across multiple fields
        return (
          (cs.project_name && cs.project_name.toLowerCase().includes(term)) || 
          (cs.property_type && cs.property_type.toLowerCase().includes(term)) ||
          (cs.state && cs.state.toLowerCase().includes(term)) ||
          (cs.project_stage && cs.project_stage.toLowerCase().includes(term)) ||
          (cs.program_name && cs.program_name.toLowerCase().includes(term)) ||
          (cs.project_number && cs.project_number.toString().includes(term))
        );
      });
    }

    // Apply Filters
    if (filter_state !== 'All' || selectedStates.length > 0) {
      if (selectedStates.length > 0) {
        filtered = filtered.filter(cs => selectedStates.includes(cs.state));
      } else {
        filtered = filtered.filter(cs => cs.state === filter_state);
      }
    }

    if (filter_property_type !== 'All' || selectedPropertyTypes.length > 0) {
      if (selectedPropertyTypes.length > 0) {
        filtered = filtered.filter(cs => selectedPropertyTypes.includes(cs.property_type));
      } else {
        filtered = filtered.filter(cs => cs.property_type === filter_property_type);
      }
    }

    if (filter_project_stage !== 'All' || selectedProjectStages.length > 0) {
      if (selectedProjectStages.length > 0) {
        filtered = filtered.filter(cs => selectedProjectStages.includes(cs.project_stage));
      } else {
        filtered = filtered.filter(cs => cs.project_stage === filter_project_stage);
      }
    }
    
    // Filter by project types (flags)
    const activeTypeFilters = Object.entries(filterTypeFlags).filter(([key, value]) => value === true).map(([key]) => key);
    if (activeTypeFilters.length > 0) { 
      filtered = filtered.filter(cs => activeTypeFilters.some(flag => cs && cs[flag] === true)); 
    }
    
    if (filter_min_amount > 0) filtered = filtered.filter(cs => cs && cs.financing_amount != null && cs.financing_amount >= filter_min_amount);
    if (filter_completion_year !== 'All') filtered = filtered.filter(cs => cs && cs.date_closed && new Date(cs.date_closed).getFullYear() === parseInt(filter_completion_year, 10));
    if (filter_historic) filtered = filtered.filter(cs => cs && cs.historic === true);

    // Apply Sorting
    try {
        filtered.sort((a, b) => {
             if (!a || !b) return 0;
              let valA = a[sortKey]; let valB = b[sortKey];
              if (typeof valA === 'string') valA = valA.toLowerCase(); if (typeof valB === 'string') valB = valB.toLowerCase();
              if (sortKey === 'date_closed') { valA = valA ? new Date(valA).getTime() : 0; valB = valB ? new Date(valB).getTime() : 0; }
              else if (sortKey === 'financing_amount') { valA = valA || 0; valB = valB || 0; }
              else if (sortKey === 'project_number') { valA = valA || 0; valB = valB || 0; }
              if (isNaN(valA)) valA = 0; if (isNaN(valB)) valB = 0;
              let comparison = 0; if (valA > valB) comparison = 1; else if (valA < valB) comparison = -1;
              return sortOrder === 'asc' ? comparison : comparison * -1;
        });
    } catch (sortError) { console.error("Error during sorting:", sortError); }
    return filtered;
  }, [allCaseStudies, filter_state, filter_property_type, filterTypeFlags, filter_project_stage, filter_min_amount, filter_completion_year, filter_historic, sortKey, sortOrder, searchTerm, selectedStates, selectedPropertyTypes, selectedProjectStages]);

  // Apply pagination to filtered results
  const paginatedCaseStudies = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return displayedCaseStudies.slice(startIndex, startIndex + itemsPerPage);
  }, [displayedCaseStudies, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(displayedCaseStudies.length / itemsPerPage);
  }, [displayedCaseStudies, itemsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStates, selectedPropertyTypes, selectedProjectStages, filterTypeFlags, filter_min_amount, filter_historic]);

  // --- Event Handlers ---
  const handleSortChange = (key) => {
    const dbKey = { 'Name': 'project_name', 'Amount': 'financing_amount', 'Date': 'date_closed' }[key] || key;
    if (dbKey === sortKey) setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(dbKey); setSortOrder('asc'); }
  };
  const handleResetFilters = () => {
      setSearchTerm('');
      setFilterState('All'); 
      setSelectedStates([]);
      setFilterPropertyType('All'); 
      setSelectedPropertyTypes([]);
      setFilterProjectStage('All');
      setSelectedProjectStages([]);
      setFilterMinAmount(0); 
      setFilterCompletionYear('All'); 
      setFilterHistoric(false);
      setFilterTypeFlags({ 
        energy_efficiency: false, 
        renewable_energy: false, 
        resilience: false,
        public_health: false,
        other: false
      });
      setStateSearchTerm(''); 
      setIsStateListOpen(false);
      setSortKey('date_closed'); 
      setSortOrder('desc');
  };
  const handleStateSelect = (stateValue) => {
      setFilterState(stateValue); setStateSearchTerm(''); setIsStateListOpen(false);
  };
  const handleTypeFlagChange = (event) => {
      const { name, checked } = event.target;
      setFilterTypeFlags(prev => ({ ...prev, [name]: checked }));
  };

  // Function to handle case study link clicks (opens in a new tab)
  const handleViewCaseStudy = (e, study) => {
    // Let the default behavior happen (link will open in new tab)
    // No need to prevent default anymore
    return true;
  };

  // --- Sidebar Component Definition (Inline) ---
  const Sidebar = () => (
    <aside className={`w-full md:w-64 lg:w-72 bg-white p-6 shadow-lg rounded-lg border border-gray-200 flex flex-col gap-6 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
         <h2 className="text-xl font-semibold text-[#1e2d60] border-b border-gray-200 pb-2 mb-2">Filter & Sort</h2>
         {/* Filter Section */}
         <div className="space-y-4">
            <h3 className="text-lg font-medium text-[#1e2d60]">Filters</h3>
            {/* State Filter - Multiselect */}
            <div ref={stateFilterRef} className="relative">
                <label htmlFor="stateSearch" className="block text-sm font-medium text-gray-600 mb-1">
                  States {selectedStates.length > 0 && `(${selectedStates.length} selected)`}
                </label>
                <div className="relative"> 
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <Search className="h-4 w-4 text-gray-400" />
                  </span>
                  <input type="text" id="stateSearch" value={stateSearchTerm} 
                    disabled={isLoading || uniqueStates.length <= 1} 
                    onChange={(e) => { setStateSearchTerm(e.target.value); setIsStateListOpen(true); }} 
                    onFocus={() => setIsStateListOpen(true)} 
                    placeholder={selectedStates.length > 0 ? `${selectedStates.length} states selected` : "Select states..."} 
                    className="w-full rounded-md border-gray-300 shadow-sm p-2 pl-10 text-sm focus:border-[#67b04b] focus:ring-[#67b04b] disabled:bg-gray-100" 
                  />
                  <button type="button" 
                    disabled={isLoading || uniqueStates.length <= 1} 
                    onClick={() => setIsStateListOpen(!isStateListOpen)} 
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 disabled:opacity-50" 
                    aria-label="Toggle state list"
                  >
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isStateListOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {isStateListOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Select States</span>
                        <button 
                          onClick={() => {
                            if (selectedStates.length > 0) {
                              setSelectedStates([]);
                              setFilterState('All');
                            }
                          }}
                          className="text-xs text-[#67b04b] hover:text-[#53903c] disabled:opacity-50"
                          disabled={selectedStates.length === 0}
                        >
                          Clear all
                        </button>
                      </div>
                    </div>
                    <ul className="py-1">
                      {filteredStates.filter(s => s !== 'All').map(s => (
                        <li key={s}>
                          <div className="flex items-center px-3 py-2 hover:bg-gray-100">
                            <input
                              id={`state-${s}`}
                              type="checkbox"
                              checked={selectedStates.includes(s)}
                              onChange={() => {
                                if (selectedStates.includes(s)) {
                                  setSelectedStates(selectedStates.filter(state => state !== s));
                                } else {
                                  setSelectedStates([...selectedStates, s]);
                                }
                                // Reset the legacy single-select when using multiselect
                                setFilterState('All');
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b]"
                            />
                            <label htmlFor={`state-${s}`} className="ml-2 text-sm text-gray-700 w-full cursor-pointer">
                              {s}
                            </label>
                          </div>
                        </li>
                      ))}
                      {filteredStates.length === 0 && <li className="px-4 py-2 text-sm text-gray-500">No states found</li>}
                    </ul>
                  </div>
                )}
            </div>
            {/* Property Type Filter - Multiselect */}
            <div>
                <label htmlFor="propertyTypeFilter" className="block text-sm font-medium text-gray-600 mb-1">
                  Property Types {selectedPropertyTypes.length > 0 && `(${selectedPropertyTypes.length} selected)`}
                </label>
                <div className="bg-[#f8f9fa] p-3 rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {uniquePropertyTypes.filter(type => type !== 'All').map(type => (
                    <div key={type} className="flex items-center mb-2 last:mb-0">
                      <input
                        id={`property-${type}`}
                        type="checkbox"
                        checked={selectedPropertyTypes.includes(type)}
                        onChange={() => {
                          if (selectedPropertyTypes.includes(type)) {
                            setSelectedPropertyTypes(selectedPropertyTypes.filter(t => t !== type));
                          } else {
                            setSelectedPropertyTypes([...selectedPropertyTypes, type]);
                          }
                          // Reset the legacy single-select
                          setFilterPropertyType('All');
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b]"
                      />
                      <label htmlFor={`property-${type}`} className="ml-2 text-sm text-gray-700 w-full cursor-pointer">
                        {type}
                      </label>
                    </div>
                  ))}
                  {selectedPropertyTypes.length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedPropertyTypes([]);
                        setFilterPropertyType('All');
                      }}
                      className="text-xs text-[#67b04b] hover:text-[#53903c] mt-2 w-full text-center"
                    >
                      Clear all
                    </button>
                  )}
                </div>
            </div>
            
            {/* Project Stage Filter - Multiselect */}
            <div>
                <label htmlFor="projectStageFilter" className="block text-sm font-medium text-gray-600 mb-1">
                  Project Stages {selectedProjectStages.length > 0 && `(${selectedProjectStages.length} selected)`}
                </label>
                <div className="bg-[#f8f9fa] p-3 rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {uniqueProjectStages.filter(stage => stage !== 'All').map(stage => (
                    <div key={stage} className="flex items-center mb-2 last:mb-0">
                      <input
                        id={`stage-${stage}`}
                        type="checkbox"
                        checked={selectedProjectStages.includes(stage)}
                        onChange={() => {
                          if (selectedProjectStages.includes(stage)) {
                            setSelectedProjectStages(selectedProjectStages.filter(s => s !== stage));
                          } else {
                            setSelectedProjectStages([...selectedProjectStages, stage]);
                          }
                          // Reset the legacy single-select
                          setFilterProjectStage('All');
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b]"
                      />
                      <label htmlFor={`stage-${stage}`} className="ml-2 text-sm text-gray-700 w-full cursor-pointer">
                        {stage}
                      </label>
                    </div>
                  ))}
                  {selectedProjectStages.length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedProjectStages([]);
                        setFilterProjectStage('All');
                      }}
                      className="text-xs text-[#67b04b] hover:text-[#53903c] mt-2 w-full text-center"
                    >
                      Clear all
                    </button>
                  )}
                </div>
            </div>
            
            {/* Project Type Checkboxes */}
            <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Project Type</label>
                <div className="space-y-2 bg-[#f8f9fa] p-3 rounded-md border border-gray-200">
                  {/* Energy Efficiency */}
                  <div className="flex items-center">
                    <input id="filter-energy_efficiency" 
                      name="energy_efficiency" 
                      type="checkbox" 
                      checked={filterTypeFlags.energy_efficiency} 
                      disabled={isLoading} 
                      onChange={handleTypeFlagChange} 
                      className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b] disabled:opacity-50"
                    />
                    <label htmlFor="filter-energy_efficiency" className="ml-2 flex items-center text-sm text-gray-700">
                      <Leaf className="w-4 h-4 mr-1.5 text-[#67b04b]" />
                      Energy Efficiency
                    </label>
                  </div>
                  
                  {/* Renewable Energy */}
                  <div className="flex items-center">
                    <input id="filter-renewable_energy" 
                      name="renewable_energy" 
                      type="checkbox" 
                      checked={filterTypeFlags.renewable_energy} 
                      disabled={isLoading} 
                      onChange={handleTypeFlagChange} 
                      className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b] disabled:opacity-50"
                    />
                    <label htmlFor="filter-renewable_energy" className="ml-2 flex items-center text-sm text-gray-700">
                      <Sun className="w-4 h-4 mr-1.5 text-amber-500" />
                      Renewable Energy
                    </label>
                  </div>
                  
                  {/* Resilience */}
                  <div className="flex items-center">
                    <input id="filter-resilience" 
                      name="resilience" 
                      type="checkbox" 
                      checked={filterTypeFlags.resilience} 
                      disabled={isLoading} 
                      onChange={handleTypeFlagChange} 
                      className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b] disabled:opacity-50"
                    />
                    <label htmlFor="filter-resilience" className="ml-2 flex items-center text-sm text-gray-700">
                      <ShieldCheck className="w-4 h-4 mr-1.5 text-indigo-500" />
                      Resilience
                    </label>
                  </div>
                  
                  {/* Public Health */}
                  <div className="flex items-center">
                    <input id="filter-public_health" 
                      name="public_health" 
                      type="checkbox" 
                      checked={filterTypeFlags.public_health} 
                      disabled={isLoading} 
                      onChange={handleTypeFlagChange} 
                      className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b] disabled:opacity-50"
                    />
                    <label htmlFor="filter-public_health" className="ml-2 flex items-center text-sm text-gray-700">
                      <Heart className="w-4 h-4 mr-1.5 text-red-500" />
                      Public Health
                    </label>
                  </div>
                  
                  {/* Other */}
                  <div className="flex items-center">
                    <input id="filter-other" 
                      name="other" 
                      type="checkbox" 
                      checked={filterTypeFlags.other} 
                      disabled={isLoading} 
                      onChange={handleTypeFlagChange} 
                      className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b] disabled:opacity-50"
                    />
                    <label htmlFor="filter-other" className="ml-2 flex items-center text-sm text-gray-700">
                      <CircleEllipsis className="w-4 h-4 mr-1.5 text-gray-500" />
                      Other
                    </label>
                  </div>
                </div>
            </div>
            
            {/* Financing Amount Slider - Replaced with Range Buttons */}
            <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Financing Amount {filter_min_amount > 0 && `(Min: ${formatCurrency(filter_min_amount)})`}
                </label>
                <div className="bg-[#f8f9fa] p-3 rounded-md border border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilterMinAmount(0)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md border ${
                        filter_min_amount === 0 
                          ? 'bg-[#67b04b] text-white border-transparent' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Any
                    </button>
                    <button
                      onClick={() => setFilterMinAmount(1000000)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md border ${
                        filter_min_amount === 1000000 
                          ? 'bg-[#67b04b] text-white border-transparent' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      $1M+
                    </button>
                    <button
                      onClick={() => setFilterMinAmount(5000000)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md border ${
                        filter_min_amount === 5000000 
                          ? 'bg-[#67b04b] text-white border-transparent' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      $5M+
                    </button>
                    <button
                      onClick={() => setFilterMinAmount(10000000)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md border ${
                        filter_min_amount === 10000000 
                          ? 'bg-[#67b04b] text-white border-transparent' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      $10M+
                    </button>
                    <button
                      onClick={() => setFilterMinAmount(25000000)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md border ${
                        filter_min_amount === 25000000 
                          ? 'bg-[#67b04b] text-white border-transparent' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      $25M+
                    </button>
                  </div>
                </div>
            </div>
            
            {/* Historic Property Checkbox */}
            <div className="flex items-center pt-2"> 
              <input id="filterIsHistoric" 
                name="filterIsHistoric" 
                type="checkbox" 
                checked={filter_historic} 
                disabled={isLoading} 
                onChange={(e) => setFilterHistoric(e.target.checked)} 
                className="h-4 w-4 rounded border-gray-300 text-[#67b04b] focus:ring-[#67b04b] disabled:opacity-50" 
              /> 
              <label htmlFor="filterIsHistoric" className="ml-2 flex items-center text-sm text-gray-700"> 
                <Landmark className="w-4 h-4 mr-1.5 text-yellow-600" /> 
                Historic Property 
              </label> 
            </div>
            
            {/* Reset Button */}
            <div className="pt-4 border-t border-gray-200">
              <button onClick={handleResetFilters} 
                disabled={isLoading} 
                className="w-full px-4 py-2 border border-[#67b04b] text-sm font-medium rounded-md shadow-sm text-[#67b04b] bg-white hover:bg-[#e8f5e4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#67b04b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset All Filters
              </button>
         </div>
         </div>
    </aside>
  );

  // --- Main Render ---
  return (
    <div className="flex flex-col min-h-screen">
      <div className="w-full max-w-screen-xl mx-auto p-4 sm:p-8 flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className={`md:block ${isSidebarOpen ? 'block w-full mb-6' : 'hidden'}`}> <Sidebar /> </div>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {/* Header & View Toggle */}
            <div className="mb-6">
              <header className="w-full mb-4">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <h1 className="text-3xl font-bold text-[#1e2d60]">Case Studies</h1>
                  
                  <div className="flex items-center gap-4 w-full md:w-auto">
                    {/* Search Bar */}
                    <div className="relative flex-grow md:max-w-md">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Search className="w-5 h-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search projects, locations, types..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#67b04b] focus:border-[#67b04b]"
                      />
                      {searchTerm && (
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    
                    {/* Mobile Menu Toggle */}
                    <button 
                      onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                      className="md:hidden p-2 rounded-md text-[#1e2d60] border border-gray-200 hover:bg-gray-100" 
                      aria-label="Toggle filters"
                    >
                      {isSidebarOpen ? <X size={20} /> : <Filter size={20} />}
                    </button>
                    
                    {/* View Toggle */}
                    <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg border border-gray-200 self-end">
                      <button 
                        onClick={() => setViewMode('grid')} 
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          viewMode === 'grid' 
                            ? 'bg-white text-[#1e2d60] shadow' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`} 
                        aria-pressed={viewMode === 'grid'}
                      >
                        <LayoutGrid size={18} className="inline mr-1.5" />
                        Grid
                      </button>
                      <button 
                        onClick={() => setViewMode('table')} 
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          viewMode === 'table' 
                            ? 'bg-white text-[#1e2d60] shadow' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`} 
                        aria-pressed={viewMode === 'table'}
                      >
                        <List size={18} className="inline mr-1.5" />
                        Table
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 mt-2">Browse successful PACE funded projects across the country.</p>
              </header>
              
              {/* Results Count */}
              <div className="text-sm text-gray-600 bg-[#f8f9fa] p-3 rounded-md border border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-center">
                  <div>
                    {isLoading 
                      ? 'Loading studies...' 
                      : `Displaying ${Math.min(paginatedCaseStudies.length, itemsPerPage)} of ${displayedCaseStudies.length} matching case studies (${allCaseStudies.length} total).`
                    }
                    {searchTerm && (
                      <span className="ml-2 font-medium text-[#1e2d60]">
                        (Search: "{searchTerm}")
                      </span>
                    )}
                    {selectedStates.length > 0 && (
                      <span className="ml-2 font-medium text-[#1e2d60]">
                        (States: {selectedStates.length})
                      </span>
                    )}
                    {selectedPropertyTypes.length > 0 && (
                      <span className="ml-2 font-medium text-[#1e2d60]">
                        (Property Types: {selectedPropertyTypes.length})
                      </span>
                    )}
                    {selectedProjectStages.length > 0 && (
                      <span className="ml-2 font-medium text-[#1e2d60]">
                        (Project Stages: {selectedProjectStages.length})
                      </span>
                    )}
                    {filter_historic && 
                      <span className="ml-2 font-medium text-[#1e2d60]">(Historic Properties Only)</span>
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center items-center py-16 px-4 bg-white rounded-lg shadow border border-gray-200">
                <Loader2 className="h-10 w-10 text-[#67b04b] animate-spin" />
                <span className="ml-3 text-lg text-[#1e2d60] font-medium">Loading Case Studies...</span>
              </div>
            )}
            
            {/* Error State */}
            {!isLoading && error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-6 rounded-lg relative mb-6 flex items-start" role="alert">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="font-bold text-red-800 block mb-1">Error Loading Data</strong>
                  <span className="block text-red-700">{error}</span>
                </div>
              </div>
            )}

            {/* Content Display Wrapper */}
            {!isLoading && !error && (
                displayedCaseStudies.length > 0 ? (
                    viewMode === 'grid' ? (
                         <div className="case-studies-grid gap-6">
                            {paginatedCaseStudies.map(study => {
                                if (!study || typeof study !== 'object') return null;
                                const propertyTypeColorClass = PROPERTY_TYPE_COLORS[study.property_type] || PROPERTY_TYPE_COLORS['Default'];
                              const hasDownloadLink = study.case_study_link && study.case_study_link !== '#';
                                return (
                                  <div key={study.id} className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 flex flex-col h-full min-w-0">
                                      {/* Card Header - Property Type Banner */}
                                      <div className="bg-[#1e2d60] text-white px-4 py-2 flex items-center justify-between min-w-0">
                                          <span className="font-medium truncate">{study.property_type || 'Property'}</span>
                                          <span className="text-sm truncate">{study.state || 'N/A'}</span>
                                      </div>
                                      
                                        <div className="p-5 flex-grow flex flex-col min-w-0">
                                          {/* Project Name - Larger and Prominent */}
                                          <h3 className="text-xl font-bold text-[#1e2d60] mb-1 line-clamp-2 min-w-0">{study.project_name || 'N/A'}</h3>
                                          
                                          {/* Project Details - With consistent height */}
                                          <div className="mb-3">
                                              {study.program_name && (
                                                  <div className="text-sm text-gray-500">
                                                      Program: {study.program_name}
                                                  </div>
                                              )}
                                          </div>
                                          
                                          {/* Project Types Badges */}
                                          <div className="flex flex-wrap gap-2 items-center mb-auto min-w-0">
                                                {Object.entries(PROJECT_TYPE_INFO).map(([key, { Icon, badgeColor, label }]) => (
                                                  study[key] === true && (
                                                      <span key={key} className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${badgeColor}`}>
                                                          <Icon className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                                                          {label}
                                                      </span>
                                                  )
                                              ))}
                                              
                                              {study.historic && (
                                                  <span title="Historic Property" className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                      <Landmark size={14} className="mr-1" />
                                                      Historic
                                                  </span>
                                              )}
                                              
                                              <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                                  {study.project_stage || 'N/A'}
                                              </span>
                                          </div>
                                          
                                          {/* Key Metrics - Fixed position at bottom of card content area */}
                                          <div className="mt-4 grid grid-cols-2 gap-4 mb-6">
                                              <div className="bg-[#f8f9fa] p-3 rounded-md border border-gray-200 h-[72px] flex flex-col justify-center">
                                                  <div className="text-xs text-gray-500 uppercase">Financing</div>
                                                  <div className="text-lg font-bold text-[#1e2d60] truncate min-w-0" title={formatCurrency(study.financing_amount, false)}>
                                                      {formatCurrency(study.financing_amount, true)}
                                                  </div>
                                            </div>
                                              <div className="bg-[#f8f9fa] p-3 rounded-md border border-gray-200 h-[72px] flex flex-col justify-center">
                                                  <div className="text-xs text-gray-500 uppercase">Closed</div>
                                                  <div className="text-lg font-bold text-[#1e2d60] truncate min-w-0" title={formatDate(study.date_closed, false)}>
                                                      {formatDate(study.date_closed)}
                                            </div>
                                              </div>
                                          </div>
                                          
                                          {/* Link Button */}
                                          {study.case_study_link ? (
                                              <a href={study.case_study_link} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer" 
                                                  className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#67b04b] hover:bg-[#53903c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#67b04b] transition-colors duration-150 ease-in-out ${!hasDownloadLink ? 'opacity-50 cursor-not-allowed' : ''}`} 
                                                  aria-disabled={!hasDownloadLink} 
                                                  title="View Case Study"
                                              >
                                                  <Download className="mr-2 h-4 w-4" />
                                                  View Case Study
                                              </a>
                                          ) : (
                                              <button disabled 
                                                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-500 bg-gray-200 cursor-not-allowed" 
                                                      aria-disabled="true"
                                              >
                                                  <Download className="mr-2 h-4 w-4" />
                                                  No Link Available
                                              </button>
                                          )}
                                        </div>
                                    </div>
                                );
                            })}
                         </div>
                     ) : (
                         <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                             <table className="min-w-full divide-y divide-gray-200">
                                 <thead className="bg-gray-50">
                                     <tr>
                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Name</th>
                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property Type</th>
                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Types</th>
                                         <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Closed</th>
                                         <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                                     </tr>
                                 </thead>
                                 <tbody className="bg-white divide-y divide-gray-200">
                                     {/* Use inline Table Row JSX */}
                                     {paginatedCaseStudies.map((study) => {
                                         if (!study || typeof study !== 'object') return null;
                                         const hasDownloadLink = study.case_study_link && study.case_study_link !== '#';
                                         const propertyTypeColorClass = PROPERTY_TYPE_COLORS[study.property_type] || PROPERTY_TYPE_COLORS['Default'];
                                         return (
                                             <tr key={study.id} className="hover:bg-gray-50 transition-colors">
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{study.project_name || 'N/A'}</td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{study.state}</td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm"> <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${propertyTypeColorClass}`}> {study.property_type || 'N/A'} </span> </td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                     <div className="flex flex-col space-y-1">
                                                         {Object.entries(PROJECT_TYPE_INFO).map(([key, { label }]) => ( 
                                                             study[key] === true && <span key={key}>{label}</span> 
                                                         ))}
                                                         {!Object.keys(PROJECT_TYPE_INFO).some(key => study[key] === true) && <span>-</span>}
                                                     </div>
                                                 </td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(study.financing_amount)}</td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" title={formatDate(study.date_closed, false)}>
                                                     {formatDate(study.date_closed)}
                                                 </td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                     {study.case_study_link ? (
                                                         <a href={study.case_study_link} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className={`text-blue-600 hover:text-blue-800 ${!hasDownloadLink ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`} 
                                                            aria-disabled={!hasDownloadLink} 
                                                            title="Open Link">
                                                             <Download size={18} />
                                                         </a>
                                                     ) : (
                                                         <span className="text-gray-400 cursor-not-allowed" title="Link unavailable"><Download size={18} /></span>
                                                     )}
                                                 </td>
                                             </tr>
                                         );
                                     })}
                                 </tbody>
                             </table>
                         </div>
                     )
                ) : (
                 <div className="case-studies-grid gap-6">
                   <div className="lg:col-span-2 xl:col-span-3 text-center py-10 px-6 bg-white rounded-lg shadow border">
                     <p className="text-gray-600 font-medium">No case studies match the current filters.</p>
                     <p className="text-sm text-gray-500 mt-2">Try adjusting your filter criteria or resetting the filters.</p>
                     <button onClick={handleResetFilters} className="mt-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                       Reset Filters
                     </button>
                   </div>
                 </div>
                )
            )}

            {/* Pagination Controls */}
            {!isLoading && !error && totalPages > 1 && (
              <div className="mt-6 flex justify-center">
                <nav className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm border border-gray-200">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-label="First page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-label="Previous page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  <div className="text-sm font-medium text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-label="Next page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-label="Last page"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 6.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0zm6 0a1 1 0 010-1.414L14.586 10l-4.293-3.293a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            )}
          </main>
      </div>
    </div>
  );
}


export default App;
